import React from 'react';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import DownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import { PopupSelect } from '@atlaskit/select';
import { getTheme } from '@atlassian/jira-common-theme';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	FireUiAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	FEATURE_GATE_ACTION_SUBJECT,
	PACKAGE_NAME,
	USER_ROLES_PICKER_TOUCHPOINT_ID,
} from '../../common/constants';
import { multipleRolesText } from '../../common/ui/user-roles/messages';
import type { Actor, CheckboxData, Role, RoleId } from '../../common/ui/user-roles/types';
import { getRoleName, getActorRoleIds } from '../../common/ui/user-roles/utils';
import DropdownFooter from './footer';
import DropdownMenuList from './menu-list';
import messages from './messages';
import DropdownOption from './option';

type Props = {
	actor: Actor;
	roles: Role[];
};

const transformProjectRolesToCheckboxOptions = (
	roles: Role[],
	selectedRoles: RoleId[],
): CheckboxData[] =>
	roles.map(
		({ id, translatedName, description }: Role): CheckboxData => ({
			id,
			title: translatedName,
			description,
			isChecked: selectedRoles.indexOf(id) !== -1,
			isDisabled: true,
		}),
	);

/**
 * A copy of [RolesPicker](`src/packages/permissions-administration/pages/users-and-roles/src/common/ui/roles-picker/index.tsx`)
 * with all options disabled, and additional content that encourages the user to upgrade their Jira Software edition.
 *
 * NOTE: If making UI changes to this component, you may also need to make the corresponding changes
 * to the original `RolesPicker` for consistency.
 */
export const FeatureGatedRolesPickerInternal = (props: Props & { defaultIsOpen?: boolean }) => {
	const { actor, roles, defaultIsOpen } = props;
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const actorRoleIds: RoleId[] = getActorRoleIds(actor);

	const handleClick = (isOpen: boolean) => {
		// `isOpen` is true when the picker is being opened, and we only want to fire a click event when the picker is being opened
		if (isOpen) {
			fireUIAnalytics(
				createAnalyticsEvent({
					actionSubject: FEATURE_GATE_ACTION_SUBJECT,
					action: 'clicked',
				}),
				{
					touchpoint: USER_ROLES_PICKER_TOUCHPOINT_ID,
				},
			);
		}
	};

	const renderDropdownLabel = (): string => {
		const numOfRoles = actorRoleIds.length;
		switch (numOfRoles) {
			case 0:
				return formatMessage(messages.noSelectedRolesLabel);
			case 1:
				return getRoleName(roles, actorRoleIds[0]);
			default:
				return formatMessage(multipleRolesText.roleMessage, { count: numOfRoles });
		}
	};

	return (
		<PopupSelect
			minMenuWidth={DROPDOWN_MENU_WIDTH_IN_PX}
			maxMenuWidth={DROPDOWN_MENU_WIDTH_IN_PX}
			options={transformProjectRolesToCheckboxOptions(roles, actorRoleIds)}
			target={({ ref, isOpen }) => (
				<CustomThemeButton
					ref={ref}
					onClick={() => handleClick(isOpen)}
					shouldFitContainer
					theme={buttonTheme}
					isSelected={isOpen}
					iconAfter={<DownIcon label={formatMessage(messages.openRolesPicker)} />}
				>
					{renderDropdownLabel()}
				</CustomThemeButton>
			)}
			isOpen={defaultIsOpen}
			components={{ Option: DropdownOption, MenuList: DropdownMenuList }}
			footer={<DropdownFooter />}
			popperProps={{
				placement: 'bottom-end',
			}}
		/>
	);
};

const FeatureGatedRolesPicker = (props: Props) => (
	<JSErrorBoundary
		id="FeatureGatedTmpUserRolesPicker"
		packageName={PACKAGE_NAME}
		teamName="growth-tako"
		fallback="unmount"
	>
		<FireUiAnalytics
			action="viewed"
			actionSubject={FEATURE_GATE_ACTION_SUBJECT}
			attributes={{ touchpoint: USER_ROLES_PICKER_TOUCHPOINT_ID }}
		/>
		<FeatureGatedRolesPickerInternal {...props} />
	</JSErrorBoundary>
);

export default FeatureGatedRolesPicker;

const DROPDOWN_MENU_WIDTH_IN_PX = 360;

const buttonTheme = getTheme({
	buttonStyles: {
		default: {
			textAlign: 'left',
		},
	},
});
